/**
 * StatCard Component
 *
 * Single-metric tile for dashboards.
 *  - label: small secondary text
 *  - value: large number (string or number)
 *  - icon: leading icon, sits in a tinted square on the left
 *  - color: theme palette color name driving the icon tint (default | primary | success | warning | error | info)
 *  - trend?: small chip on the right (e.g. "+12%")
 *  - hint?: helper text under the value
 *  - loading?: shows skeleton placeholders
 */
import { Box, Card, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export type StatCardColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  icon: ReactNode;
  color?: StatCardColor;
  trend?: { label: string; tone?: 'up' | 'down' | 'flat' };
  hint?: ReactNode;
  loading?: boolean;
  onClick?: () => void;
}

const tintBg: Record<StatCardColor, (mode: 'light' | 'dark') => string> = {
  default: (m) => (m === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
  primary: (m) => (m === 'dark' ? 'rgba(99,102,241,0.20)' : 'rgba(79,70,229,0.10)'),
  secondary: (m) => (m === 'dark' ? 'rgba(6,182,212,0.20)' : 'rgba(6,182,212,0.12)'),
  success: (m) => (m === 'dark' ? 'rgba(52,211,153,0.18)' : 'rgba(16,185,129,0.12)'),
  warning: (m) => (m === 'dark' ? 'rgba(251,191,36,0.18)' : 'rgba(245,158,11,0.14)'),
  error: (m) => (m === 'dark' ? 'rgba(248,113,113,0.18)' : 'rgba(239,68,68,0.12)'),
  info: (m) => (m === 'dark' ? 'rgba(56,189,248,0.18)' : 'rgba(14,165,233,0.12)'),
};

const toneColor: Record<NonNullable<StatCardProps['trend']>['tone'] & string, StatCardColor> = {
  up: 'success',
  down: 'error',
  flat: 'default',
};

export function StatCard({
  label,
  value,
  icon,
  color = 'primary',
  trend,
  hint,
  loading = false,
  onClick,
}: StatCardProps) {
  return (
    <Card
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        // Very subtle gradient that lifts the tile off the page background.
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(255,255,255,0.02) 100%)`
            : `linear-gradient(135deg, #ffffff 0%, ${tintBg[color]('light')} 100%)`,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2.25 }, '&:last-child': { pb: { xs: 1.5, sm: 2.25 } } }}>
        {/* xs: vertical stack (icon top-left, value+label a la derecha) para que los
            labels largos ("Enrollments pendientes", "Sesiones activas", "Agentes
            conectados") no se corten al pasar a 2 columnas en mobile.
            sm+: row horizontal como antes. */}
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 1.5 }}
          alignItems="center"
        >
          <Box
            sx={{
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
              borderRadius: { xs: 2, sm: 2.5 },
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => tintBg[color](theme.palette.mode as 'light' | 'dark'),
              color: `${color}.main`,
              flexShrink: 0,
              '& > svg': { fontSize: { xs: 20, sm: 24 } },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {loading ? (
              <Skeleton variant="text" width={70} height={32} />
            ) : (
              <Typography
                variant="h4"
                component="div"
                fontWeight={700}
                sx={{ lineHeight: 1.1, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}
              >
                {value}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.25,
                mt: 0.25,
                // 2 líneas en mobile/tablet (2-col grid deja poco ancho), 1 en
                // desktop (4-col grid tiene espacio de sobra). Sin truncate feo.
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                display: '-webkit-box',
                WebkitLineClamp: { xs: 2, sm: 2, md: 1 },
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {label}
            </Typography>
          </Box>
          {trend && !loading && (
            <Chip
              label={trend.label}
              size="small"
              color={toneColor[trend.tone ?? 'flat']}
              variant="outlined"
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
            />
          )}
        </Stack>
        {hint && !loading && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: { xs: 0.75, sm: 1.25 }, lineHeight: 1.4 }}
          >
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default StatCard;
